/* WHAT IS IN BRILL — taught to Ask Brill (her ask, 2026-10-22).
 *
 * She asked plain Ask Brill to "create a masterclass presentation" and it
 * started building one in the chat, because Ask Brill had never heard of
 * Presentations: its instructions list the nine Accelerator modules and
 * nothing in Momentum. The chat cannot keep a deck; the tool can.
 *
 * So every Ask Brill message (every coach too) carries a short list of the
 * Momentum tools THIS account can open — live for her tier, admin-only ones for
 * the admin alone — with one line on what each makes. When she asks for one of
 * those things, Brill says which tool builds it and ends with
 *
 *   <<<OPEN_TOOL:talks>>>
 *
 * which shows as an "Open Presentations" button under the reply. A marker
 * naming a tool NOT in her list shows nothing: Brill can never send her to a
 * door she cannot open. The marker never shows as text (cleanMessageContent).
 *
 * Ids are MomentumHub's BUCKETS ids (`talks`, `website`…), which never move.
 * COST: ~250 tokens on each Ask Brill message, cached with the rest.
 */

/* What she makes in each tool, in her words where she has them. A tool with
   no line here is still listed, by its name alone. */
export const TOOL_JOBS = {
  offer_ladder: "her offers by rung (Trust Building, Core, Continuation): name, promise, price, and where each one sends people",
  website: "her website, page by page and section by section, plus the brief she hands her site builder",
  talks: "presentations: masterclasses, signature talks and kickoff decks, slide by slide with speaker notes",
  lesson_plan: "her course, module by module",
  outbound: "outreach sequences (LinkedIn, email, DMs), each touch written and kept",
  salescall: "call scripts and video scripts, section by section",
  social: "social posts for LinkedIn, Instagram and Facebook, multi-day arcs and the content calendar; sends drafts to Go High Level",
  newsletter: "newsletters and email pieces",
  ctas: "the meeting and booking links her offers point people to",
  library: "her ideas, stories, testimonials and objections, kept for every other tool to use",
};

export const OPEN_TOOL_RE = /<<<OPEN_TOOL:\s*([a-z_]+)\s*>>>/i;

/**
 * The tools this account can open, as { id, navId, label, group, job }.
 * @param buckets  MomentumHub's BUCKETS
 * @param statusOf toolStatus(item) for this account: "live" | "soon"
 */
export function openableTools(buckets = [], statusOf = (it) => it.status) {
  return (buckets || []).flatMap((b) => (b.items || [])
    .filter((it) => statusOf(it) === "live")
    .map((it) => ({ id: it.id, navId: it.route || `m/${it.id}`, label: it.label, group: b.label, job: TOOL_JOBS[it.id] || "" })));
}

/** The block Ask Brill is handed, or null when she has no Momentum tools. */
export function brillToolsBlock(tools = []) {
  if (!tools?.length) return null;
  const lines = tools.map((t) => `- ${t.label} (${t.group}) — ${t.job || t.label} [id: ${t.id}]`);
  return `[BRILL_TOOLS — read only, do not reference as a conversation]
Brill has tools that BUILD and KEEP her work. These are the ones she can open:
${lines.join("\n")}

When she asks you to make something one of these tools makes, tell her in one or two lines which tool does it and why it is better there: it is saved, she can edit it, and it is built from her foundation. Then end your reply with the marker on its own line, using that tool's id:
<<<OPEN_TOOL:id>>>
It shows her a button that opens the tool. One marker per reply, only for a tool in this list, and only when the tool is the right place — if she wants to think something through first, help her here and offer the tool when it is ready. Never say you have opened it; the button is hers to press.`;
}

/** The tool a reply offers, from her list — or null. */
export function toolOffer(content, tools = []) {
  const m = String(content || "").match(OPEN_TOOL_RE);
  if (!m) return null;
  return (tools || []).find((t) => t.id === m[1].toLowerCase()) || null;
}
